import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { XIcon } from "lucide-react";
const DEFAULT_ID = "IxRuvTk8r-A";
const EASE = [0.23, 1, 0.32, 1] as const;
interface ReelModalProps {
  open: boolean;
  onClose: () => void;
  videoId?: string;
  title?: string;
}
export function ReelModal({
  open,
  onClose,
  videoId = DEFAULT_ID,
  title = "Launch film"
}: ReelModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduce = useReducedMotion();
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      prevFocus?.focus();
    };
  }, [open, onClose]);
  if (typeof document === "undefined") return null;

  // Portal keeps the popup full-screen even when opened from a scaled card.
  return createPortal(<AnimatePresence data-id="element-126" data-mp="3thauod3n61z5">
      {open && <motion.div role="dialog" aria-modal="true" aria-label={`Brandhero AI — ${title}`} className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-10" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onClose} data-id="element-127" data-mp="3tghkf25h4uc9">
          <motion.div className="relative w-full max-w-[1200px]" initial={{
        opacity: 0,
        scale: reduce ? 1 : 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: reduce ? 1 : 0.96
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} onClick={e => e.stopPropagation()} data-id="element-128" data-mp="293imyrn88a7l">
            <div className="mb-3 flex items-center justify-between font-mono text-[11px] tracking-[0.12em] md:text-[12px]" data-id="element-129" data-mp="2fz22tlumhzp6">
              <span className="flex items-center gap-2 uppercase text-signal" data-id="element-130" data-mp="2axmffn4z3h3m">
                <span className="rec-dot is-round inline-block h-1.5 w-1.5 bg-signal" aria-hidden="true" data-id="element-131" data-mp="2oxdxbyym1hmw" />
                BRANDHERO AI — {title}
              </span>
              <button ref={closeRef} type="button" onClick={onClose} aria-label={`Close ${title}`} className="flex h-10 items-center gap-2 border border-hairline px-3 text-white transition-colors duration-150 hover:border-white hover:bg-white/10" data-id="element-132" data-mp="2yp1l9pvykgjm">
                CLOSE
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-133" data-mp="33lrzs8s4742x" />
              </button>
            </div>
            <div className="relative aspect-video w-full border border-hairline bg-black" data-id="element-134" data-mp="3ma2jkh18cxtj">
              <iframe src={embedUrl} title={`Brandhero AI — ${title}`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="absolute inset-0 h-full w-full border-0" data-id="element-135" data-mp="3r8vuc1p4uaq0" />
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}